import { useEffect, useState } from "react";
import { api, apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Spinner, Btn, Empty } from "@/components/common";
import { onLive } from "@/lib/live";
import { timeAgo } from "@/lib/ui";
import { Radar, Plus, X, Wifi, Scan } from "lucide-react";
import { toast } from "sonner";

const TYPE_ICON = { Printer: "🖨️", Switch: "🔀", "Wireless AP": "📶", NAS: "🗄️", Server: "🖥️", UPS: "🔋", "IP Camera": "📷" };

export default function NetworkDiscovery() {
  const { can } = useAuth();
  const [tab, setTab] = useState("Discovered");
  const [devices, setDevices] = useState(null);
  const [probes, setProbes] = useState([]);
  const [allDevices, setAllDevices] = useState([]);
  const [show, setShow] = useState(false);
  const [f, setF] = useState({ device_id: "", subnets: "10.10.10.0/24" });

  const load = () => {
    api.get("/network/devices").then((r) => setDevices(r.data)).catch(() => setDevices([]));
    api.get("/network/probes").then((r) => setProbes(r.data)).catch(() => {});
    api.get("/devices").then((r) => setAllDevices(r.data)).catch(() => {});
  };
  useEffect(() => { load(); return onLive((e) => { if (e.type === "network" || e.type === "alert") load(); }); }, []);

  const addProbe = async () => {
    try { await api.post("/network/probes", { device_id: f.device_id, subnets: f.subnets.split(",").map((s) => s.trim()).filter(Boolean) });
      toast.success("Probe configured"); setShow(false); load(); } catch (e) { toast.error(apiErr(e)); }
  };
  const scan = async (pid) => { try { await api.post(`/network/scan?device_id=${pid}`); toast.success("Scan dispatched to probe"); } catch (e) { toast.error(apiErr(e)); } };
  const classify = async (nid, c) => { await api.patch(`/network/devices/${nid}`, { classification: c }); toast.success("Classified"); load(); };

  if (!devices) return <div className="p-6"><Spinner /></div>;
  return (
    <div className="p-6 fade-up">
      <PageHeader title="Network Discovery" subtitle="Authorized subnet scanning via agent probes" testid="network-page"
        actions={can("devices:modify") && <Btn data-testid="add-probe-btn" onClick={() => setShow(true)}><Plus size={15} /> Configure Probe</Btn>} />

      <div className="flex gap-1 border-b border-slate-800 mb-4">
        {["Discovered", "Probes"].map((t) => <button key={t} onClick={() => setTab(t)}
          className={"px-3 py-2 text-[13px] font-medium border-b-2 -mb-px " + (tab === t ? "border-cyan-400 text-cyan-300" : "border-transparent text-slate-400 hover:text-slate-200")}>{t} {t === "Discovered" && devices.length ? `(${devices.length})` : ""}</button>)}
      </div>

      {tab === "Discovered" && (devices.length === 0 ? <Empty icon={Radar} title="No devices discovered" hint="Configure an agent as a network probe with authorized subnets, then run a scan." /> : (
        <div className="card-surface rounded-lg overflow-hidden">
          <table className="w-full text-left text-[13px]" data-testid="network-devices-table">
            <thead><tr className="text-[10px] uppercase tracking-wider text-slate-500 bg-slate-900/80 border-b border-slate-800">
              <th className="px-3 py-2">Type</th><th className="px-3 py-2">IP</th><th className="px-3 py-2">Hostname</th><th className="px-3 py-2">MAC / Vendor</th><th className="px-3 py-2">OS</th><th className="px-3 py-2">Services</th><th className="px-3 py-2">Seen</th><th className="px-3 py-2">Class</th></tr></thead>
            <tbody className="divide-y divide-slate-800/60">
              {devices.map((d) => (
                <tr key={d.id} className="hover-row" data-testid="network-device-row">
                  <td className="px-3 py-2">{TYPE_ICON[d.device_type] || "❓"} <span className="text-slate-300">{d.device_type}</span></td>
                  <td className="px-3 py-2 font-mono text-cyan-300">{d.ip}</td>
                  <td className="px-3 py-2 text-slate-200">{d.hostname}</td>
                  <td className="px-3 py-2 font-mono text-slate-500">{d.mac}<div className="text-[10px] text-slate-600">{d.mac_vendor}</div></td>
                  <td className="px-3 py-2 text-slate-400">{d.os_estimate}</td>
                  <td className="px-3 py-2 font-mono text-slate-500">{(d.open_services || []).join(", ")}</td>
                  <td className="px-3 py-2 text-slate-500">{timeAgo(d.last_seen)}</td>
                  <td className="px-3 py-2">
                    {d.is_new ? <span className="text-[10px] font-bold uppercase bg-amber-950/60 text-amber-300 rounded px-1.5 py-0.5">NEW</span>
                      : <span className="text-[10px] text-slate-500">{d.classification}</span>}
                    {d.is_new && can("devices:modify") && <button onClick={() => classify(d.id, "known_network")} className="ml-2 text-[10px] text-cyan-400">acknowledge</button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}

      {tab === "Probes" && (probes.length === 0 ? <Empty icon={Wifi} title="No probes configured" hint="Any online agent can act as a network probe for its site." /> : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {probes.map((p) => (
            <div key={p.id} className="card-surface rounded-lg p-4" data-testid="probe-card">
              <div className="flex items-center gap-2 mb-1"><Wifi size={16} className="text-cyan-400" /><span className="font-heading font-bold text-slate-100 font-mono">{p.hostname}</span></div>
              <div className="text-[12px] text-slate-500 mb-2">{p.customer_name} · last scan {p.last_scan ? timeAgo(p.last_scan) : "never"}</div>
              <div className="flex flex-wrap gap-1 mb-3">{(p.allowed_subnets || []).map((s) => <span key={s} className="text-[11px] font-mono bg-slate-800 text-slate-300 rounded px-1.5 py-0.5">{s}</span>)}</div>
              {can("devices:modify") && <Btn data-testid="scan-btn" onClick={() => scan(p.id)}><Scan size={13} /> Run Scan</Btn>}
            </div>
          ))}
        </div>
      ))}

      {show && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60" onClick={() => setShow(false)}>
          <div className="card-surface rounded-xl p-5 w-full max-w-md mx-4" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-3"><h2 className="font-heading font-bold text-slate-100">Configure Network Probe</h2><button onClick={() => setShow(false)} className="text-slate-500 hover:text-slate-200"><X size={18} /></button></div>
            <label className="text-[12px] text-slate-400">Agent (probe)</label>
            <select data-testid="probe-device" value={f.device_id} onChange={(e) => setF({ ...f, device_id: e.target.value })} className="mt-1 mb-3 w-full bg-slate-900 border border-slate-700 rounded-md px-3 py-2 text-sm text-slate-100 outline-none">
              <option value="">Select an online agent…</option>
              {allDevices.map((d) => <option key={d.id} value={d.id}>{d.hostname} · {d.customer_name}</option>)}
            </select>
            <label className="text-[12px] text-slate-400">Authorized subnets (comma-separated)</label>
            <input data-testid="probe-subnets" value={f.subnets} onChange={(e) => setF({ ...f, subnets: e.target.value })} className="mt-1 mb-2 w-full bg-slate-900 border border-slate-700 rounded-md px-3 py-2 text-sm text-slate-100 font-mono outline-none" />
            <p className="text-[11px] text-slate-600 mb-4">Only these ranges will be scanned. No Internet-wide scanning is permitted.</p>
            <div className="flex justify-end gap-2"><Btn variant="ghost" onClick={() => setShow(false)}>Cancel</Btn><Btn data-testid="probe-save-btn" onClick={addProbe} disabled={!f.device_id}>Save Probe</Btn></div>
          </div>
        </div>
      )}
    </div>
  );
}
